/**
 * CMS API Client
 *
 * Lightweight client for the TraceCms backend API. Authenticates via a
 * Bearer token resolved through a getter on every request, so token rotation
 * is picked up automatically.
 */

import type {
    AssetInsertResponseDto,
    AssetResponseDto,
    SceneAccessorRequestDto,
    SceneAccessorResponseDto,
    SceneContentCollectionDto,
    SceneContentResponseDto,
    SceneResponseDto,
    TagParentResponseDto,
} from 'trace-viewer/cms';

// ── Types ──────────────────────────────────────────────────────────

/**
 * One entry of a scene's content folder, as `POST /scenes/{id}/files` answers.
 *
 * `uri` is **relative** (`assets/<sha256>.<ext>`) on purpose: a reader resolves
 * it against the document's own URL, which is what lets a folder be copied,
 * downloaded, zipped and re-uploaded with nothing rewritten (spec §2.3).
 */
export interface SceneFileEntry {
    uri: string;
    mimeType?: string | null;
    size?: number;
    /** True when the folder already held these bytes — a dedupe hit, not an error. */
    alreadyExisted?: boolean;
}

// ── Client ─────────────────────────────────────────────────────────

/** Returns a Bearer token, or null if the user is not authenticated. */
export type TokenGetter = () => string | null;

export class CmsClient {
    private _getToken: TokenGetter;
    private _apiBaseUrl: string;
    private _blobBaseUrl: string;

    constructor(getToken: TokenGetter, apiBaseUrl: string, blobBaseUrl: string) {
        this._getToken = getToken;
        this._apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
        this._blobBaseUrl = blobBaseUrl.replace(/\/$/, '');
    }

    get apiBaseUrl(): string {
        return this._apiBaseUrl;
    }

    async listAssets(kind: string): Promise<AssetResponseDto[]> {
        return this._fetch(`/assets?kind=${encodeURIComponent(kind)}`, { redirect: 'error' });
    }

    async getAsset(assetId: string): Promise<AssetResponseDto> {
        return this._fetch(`/assets/${encodeURIComponent(assetId)}`, { redirect: 'error' });
    }

    async getAssetTags(): Promise<TagParentResponseDto[]> {
        return this._fetch('/assets/tags', { redirect: 'error' });
    }

    async saveAsset(parts: FormData, assetId?: string): Promise<AssetInsertResponseDto | AssetResponseDto> {
        return this._fetch(assetId ? `/assets/${encodeURIComponent(assetId)}` : '/assets', {
            method: assetId ? 'PATCH' : 'POST', body: parts, redirect: 'error',
        });
    }

    // ── API methods ────────────────────────────────────────────────

    /**
     * Fetch scene metadata.
     */
    async getScene(sceneId: string): Promise<SceneResponseDto> {
        return this._fetch<SceneResponseDto>(`/scenes/${sceneId}`);
    }

    /**
     * Fetch all content rows for a scene.
     *
     * The `?includeReferenceContent=true` probe is gone with the row type it
     * asked for: `ReferenceContent` is rejected on write (TraceBackend #915)
     * and the editor no longer imports it. A scene's contents are the
     * `RootContent` row and whatever else the backend keeps there.
     *
     * Returns the row array directly — callers that need download URLs use
     * `contentDownloadUrl` from `trace-viewer/cms`, which respects each row's
     * fully-qualified `url` field and falls back to a synthesized URL.
     */
    async listSceneContents(sceneId: string): Promise<SceneContentResponseDto[]> {
        const collection = await this._fetch<SceneContentCollectionDto>(
            `/scenes/${sceneId}/contents`,
        );
        return collection.contents ?? [];
    }

    /**
     * Import one file into the scene's content folder
     * (`POST /scenes/{id}/files`, CONTENT_MODEL_SPEC §2.4).
     *
     * The server hashes the bytes and stores them at
     * `trace-content/scenes/{id}/assets/<sha256>.<ext>`, answering with the
     * **relative** uri the scene document must reference them by. Re-uploading
     * the same bytes is not an error — it answers 200 with the same entry and
     * `alreadyExisted: true` — so a caller that is unsure whether the folder
     * already holds a file can just send it.
     *
     * `fileName` matters: the server's extension rule is filename-authoritative
     * for the text formats (`gltf`, `json`, fonts) and falls back to the
     * filename whenever the mime is `application/octet-stream`.
     */
    async uploadSceneFile(sceneId: string, file: Blob, fileName: string): Promise<SceneFileEntry> {
        const formData = new FormData();
        formData.append('File', file, fileName);

        const url = `${this._apiBaseUrl}/scenes/${sceneId}/files`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this._authHeaders(),
            body: formData,
        });

        if (response.status === 401) {
            throw new CmsAuthError('Not authenticated. Please sign in to import files.');
        }
        if (!response.ok) {
            throw new CmsApiError(
                `File import failed for "${fileName}": ${response.status} ${response.statusText}`,
                response.status,
            );
        }

        const entry = await response.json() as SceneFileEntry;
        if (!entry?.uri) {
            throw new Error(`File import for "${fileName}" returned no uri`);
        }
        return entry;
    }

    /**
     * Update a single SceneAccessor (PATCH /scenes/{sceneId}/accessors/{accessorId}).
     *
     * The backend's "PartialUpdate" actually requires the full
     * `SceneAccessorRequestDto` body — caller must pass the complete shape
     * with the new value spliced in, not just the changed field.
     */
    async updateSceneAccessor(
        sceneId: string,
        accessorId: string,
        body: SceneAccessorRequestDto,
    ): Promise<SceneAccessorResponseDto> {
        const url = `${this._apiBaseUrl}/scenes/${sceneId}/accessors/${accessorId}`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                ...this._authHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (response.status === 401) {
            throw new CmsAuthError('Not authenticated. Please sign in.');
        }
        if (!response.ok) {
            throw new CmsApiError(`Accessor update failed: ${response.status} ${response.statusText}`, response.status);
        }
        return await response.json() as SceneAccessorResponseDto;
    }

    /**
     * Upload a new content entry to a scene.
     * Posts multipart form-data with the file blob and metadata.
     */
    async uploadContent(sceneId: string, file: Blob, metadata: {
        name?: string;
        contentType?: string;
        version: string;
    }): Promise<SceneContentCollectionDto> {
        const formData = new FormData();
        formData.append('File', file, metadata.name ?? 'scene.trace');
        formData.append('Version', metadata.version);
        if (metadata.contentType) formData.append('ContentType', metadata.contentType);
        if (metadata.name) formData.append('Name', metadata.name);

        const url = `${this._apiBaseUrl}/scenes/${sceneId}/contents`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this._authHeaders(),
            body: formData,
        });

        if (response.status === 401) {
            throw new CmsAuthError('Not authenticated. Please sign in to publish.');
        }

        if (!response.ok) {
            throw new CmsApiError(`Upload failed: ${response.status} ${response.statusText}`, response.status);
        }

        return await response.json() as SceneContentCollectionDto;
    }

    // ── Internal ───────────────────────────────────────────────────

    private _authHeaders(): Record<string, string> {
        const token = this._getToken();
        if (!token) {
            throw new CmsAuthError('Not authenticated. Please sign in.');
        }
        return { 'Authorization': `Bearer ${token}` };
    }

    private async _fetch<T>(path: string, init: RequestInit = {}): Promise<T> {
        const url = `${this._apiBaseUrl}${path}`;
        const response = await fetch(url, {
            ...init,
            headers: {
                ...this._authHeaders(),
                ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
            },
        });

        if (response.status === 401) {
            throw new CmsAuthError('Not authenticated. Please sign in.');
        }

        if (!response.ok) {
            throw new CmsApiError(`API request failed: ${response.status} ${response.statusText}`, response.status);
        }

        return await response.json() as T;
    }
}

// ── Errors ─────────────────────────────────────────────────────────

export class CmsAuthError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'CmsAuthError';
    }
}

export class CmsApiError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.name = 'CmsApiError';
        this.status = status;
    }
}

// ── URL params ─────────────────────────────────────────────────────

/**
 * What scene to load, parsed from the page URL. The token used for
 * authentication is no longer carried in the URL — the user must be signed
 * into the catalog (refresh-token cookie) for CMS API calls to succeed.
 */
export interface CmsConnectionParams {
    apiBaseUrl: string;
    sceneId: string;
}

/**
 * Parse CMS scene-targeting params from the URL query string. Returns null
 * when the URL doesn't reference a CMS scene.
 *
 * Two equivalent shapes are accepted:
 *   - `?src=https://api.example.com/scenes/{id}/contents`
 *   - `?scene={id}&api=https://api.example.com`
 */
export function parseCmsParams(search: string): CmsConnectionParams | null {
    const params = new URLSearchParams(search);

    const src = params.get('src');
    if (src) {
        const parsed = parseCmsUrl(src);
        if (parsed) return parsed;
    }

    const sceneId = params.get('scene');
    const api = params.get('api');
    if (sceneId && api) {
        return { apiBaseUrl: api, sceneId };
    }

    return null;
}

/**
 * Parse a CMS URL to extract API base and scene ID.
 *
 * Expected pattern:
 * - https://api.example.com/scenes/{sceneId}/contents
 */
function parseCmsUrl(url: string): CmsConnectionParams | null {
    try {
        const parsed = new URL(url);
        const match = parsed.pathname.match(/\/scenes\/([^/]+)\/contents\/?$/);
        if (!match) return null;

        // API base = everything before /scenes/...
        const apiBase = parsed.origin + parsed.pathname.substring(0, parsed.pathname.indexOf('/scenes/'));

        return {
            apiBaseUrl: apiBase || parsed.origin,
            sceneId: match[1],
        };
    } catch {
        return null;
    }
}
